// pages/strategic-playbook/tools/pre-mortem.js
// ════════════════════════════════════════════════════
// PRE-MORTEM GENERATOR
// ════════════════════════════════════════════════════
// Munger inversion as a one-click tool. Type a decision,
// get 7 failure modes ranked by probability with warning
// signals + mitigation per mode.
//
// Connects to: Munger (inversion)
// ════════════════════════════════════════════════════

import { useState } from 'react';
import Head from 'next/head';
import Link from 'next/link';

const TIME_HORIZONS = [
  { id: 'short', label: 'Short-term', detail: 'Days to weeks' },
  { id: 'medium', label: 'Medium-term', detail: 'Months to a year' },
  { id: 'long', label: 'Long-term', detail: 'Multi-year' },
  { id: 'multi-decade', label: 'Multi-decade', detail: '10+ years' },
];

const PILLARS = [
  { id: 1, label: 'Vision' },
  { id: 2, label: 'Talent' },
  { id: 3, label: 'Development' },
  { id: 4, label: 'Strategy' },
  { id: 5, label: 'Decision Architecture' },
  { id: 6, label: 'Resilience' },
  { id: 7, label: 'Culture' },
  { id: 8, label: 'Legacy' },
];

export default function PreMortemTool() {
  const [decision, setDecision] = useState('');
  const [timeHorizon, setTimeHorizon] = useState('medium');
  const [selectedPillars, setSelectedPillars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const togglePillar = (id) => {
    setSelectedPillars((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  async function generate() {
    if (decision.trim().length < 10) {
      setError('Describe the decision in more detail (at least 10 characters).');
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await fetch('/api/strategic-playbook/pre-mortem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          decision,
          pillars: selectedPillars,
          timeHorizon: TIME_HORIZONS.find((t) => t.id === timeHorizon)?.label || timeHorizon,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to generate');
      }
      setResult(data);
    } catch (err) {
      setError(err.message || 'Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="sp-root">
      <Head>
        <title>Pre-Mortem Generator · Strategic Intelligence</title>
      </Head>

      <div className="sp-page">
        <ToolTopbar />

        <div style={{ marginBottom: 32 }}>
          <Link href="/strategic-playbook/tools" className="sp-reader-back" style={{ marginBottom: 16 }}>
            ← Tools
          </Link>
          <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.20em', color: 'var(--sp-aged-brass)', marginBottom: 14 }}>
            Strategic Framing · Generator
          </div>
          <h1 style={{ fontFamily: 'var(--sp-font-serif)', fontWeight: 500, fontSize: 32, lineHeight: 1.2, color: 'var(--sp-warm-cream)', margin: '0 0 14px 0', letterSpacing: '-0.01em' }}>
            Pre-Mortem Generator
          </h1>
          <p style={{ fontSize: 14, lineHeight: 1.65, color: 'rgba(232, 221, 196, 0.78)', maxWidth: 720, margin: 0 }}>
            Munger&apos;s inversion as a tool: <em>&ldquo;What would guarantee failure?&rdquo;</em>{' '}
            Type your decision below. Get 7 failure modes ranked by probability, with
            warning signals and mitigation per mode. Forces the failure-thinking most
            leaders skip until it&apos;s too late.
          </p>
        </div>

        {/* Input form */}
        <div
          style={{
            padding: '28px',
            background: 'var(--sp-soft-charcoal)',
            border: '1px solid var(--sp-divider)',
            borderRadius: 'var(--sp-radius-card)',
            marginBottom: 24,
          }}
        >
          <h3 style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--sp-aged-brass)', margin: '0 0 22px 0' }}>
            The Decision
          </h3>

          <textarea
            value={decision}
            onChange={(e) => setDecision(e.target.value)}
            placeholder="Describe what you're considering. Be specific. Example: 'Hiring a co-founder for my B2B SaaS company. Met them through a mutual contact 3 months ago. Strong technical background but limited commercial experience. Considering 15% equity vesting over 4 years.'"
            rows={6}
            style={{
              width: '100%',
              padding: '14px 18px',
              background: 'var(--sp-charcoal-black)',
              border: '1px solid var(--sp-divider)',
              borderRadius: 'var(--sp-radius)',
              color: 'var(--sp-warm-cream)',
              fontFamily: 'var(--sp-font-body)',
              fontSize: 14,
              lineHeight: 1.6,
              resize: 'vertical',
              outline: 'none',
              marginBottom: 18,
            }}
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }} className="pre-mortem-options">
            {/* Time horizon */}
            <div>
              <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(232, 221, 196, 0.55)', marginBottom: 10 }}>
                Time Horizon
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {TIME_HORIZONS.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTimeHorizon(t.id)}
                    style={{
                      textAlign: 'left',
                      padding: '8px 12px',
                      background: timeHorizon === t.id ? 'var(--sp-brass-bg)' : 'transparent',
                      border: `1px solid ${timeHorizon === t.id ? 'var(--sp-aged-brass)' : 'var(--sp-divider)'}`,
                      borderRadius: 'var(--sp-radius)',
                      cursor: 'pointer',
                      fontSize: 13,
                      color: timeHorizon === t.id ? 'var(--sp-warm-cream)' : 'rgba(232, 221, 196, 0.75)',
                    }}
                  >
                    <span style={{ fontFamily: 'var(--sp-font-serif)' }}>{t.label}</span>
                    <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, color: 'rgba(232, 221, 196, 0.45)', marginLeft: 8 }}>
                      {t.detail}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Pillars */}
            <div>
              <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'rgba(232, 221, 196, 0.55)', marginBottom: 10 }}>
                Pillars Touched (Optional)
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {PILLARS.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => togglePillar(p.id)}
                    className={`sp-tag sp-pillar-bg-${p.id} sp-pillar-${p.id}`}
                    style={{
                      cursor: 'pointer',
                      opacity: selectedPillars.length === 0 || selectedPillars.includes(p.id) ? 1 : 0.4,
                      outline: selectedPillars.includes(p.id) ? '1px solid var(--sp-aged-brass)' : 'none',
                      outlineOffset: 1,
                      border: 'none',
                      fontFamily: 'var(--sp-font-mono)',
                      fontSize: 9,
                      textTransform: 'uppercase',
                      letterSpacing: '0.10em',
                      padding: '4px 10px',
                    }}
                  >
                    P{p.id} {p.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 24, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={generate}
              disabled={loading || decision.trim().length < 10}
              style={{
                padding: '12px 24px',
                background: loading || decision.trim().length < 10 ? 'transparent' : 'var(--sp-brass-bg)',
                border: `1px solid ${loading || decision.trim().length < 10 ? 'var(--sp-divider)' : 'var(--sp-aged-brass)'}`,
                color: loading || decision.trim().length < 10 ? 'rgba(232, 221, 196, 0.40)' : 'var(--sp-aged-brass)',
                borderRadius: 'var(--sp-radius-card)',
                fontFamily: 'var(--sp-font-mono)',
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: '0.16em',
                cursor: loading || decision.trim().length < 10 ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Generating...' : 'Run Pre-Mortem →'}
            </button>
            {error && (
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--sp-oxblood)',
                  fontFamily: 'var(--sp-font-mono)',
                  letterSpacing: '0.04em',
                }}
              >
                {error}
              </div>
            )}
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div
            style={{
              padding: '48px',
              background: 'var(--sp-soft-charcoal)',
              border: '1px solid var(--sp-divider)',
              borderRadius: 'var(--sp-radius-card)',
              textAlign: 'center',
              fontFamily: 'var(--sp-font-serif)',
              fontStyle: 'italic',
              color: 'var(--sp-aged-brass)',
              fontSize: 16,
            }}
          >
            Inverting the question...
            <div
              style={{
                fontFamily: 'var(--sp-font-mono)',
                fontStyle: 'normal',
                fontSize: 11,
                color: 'rgba(232, 221, 196, 0.50)',
                marginTop: 8,
                letterSpacing: '0.10em',
                textTransform: 'uppercase',
              }}
            >
              Munger frame · 7 failure modes
            </div>
          </div>
        )}

        {/* Results */}
        {result && !loading && (
          <div>
            {/* Summary */}
            <div
              style={{
                padding: '28px',
                background: 'linear-gradient(135deg, var(--sp-card-charcoal) 0%, var(--sp-soft-charcoal) 100%)',
                border: '1px solid var(--sp-aged-brass)',
                borderRadius: 'var(--sp-radius-card)',
                marginBottom: 20,
              }}
            >
              <div style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.18em', color: 'var(--sp-aged-brass)', marginBottom: 14 }}>
                Dominant Pattern
              </div>
              <div
                style={{
                  fontFamily: 'var(--sp-font-serif)',
                  fontStyle: 'italic',
                  fontSize: 17,
                  lineHeight: 1.55,
                  color: 'var(--sp-warm-cream)',
                  borderLeft: '2px solid var(--sp-aged-brass)',
                  paddingLeft: 16,
                }}
              >
                {result.summary}
              </div>
            </div>

            {/* Failure mode cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {(result.failureModes || []).map((fm, i) => (
                <FailureModeCard key={i} failureMode={fm} index={i + 1} />
              ))}
            </div>

            {/* Connected dossier */}
            <div style={{ marginTop: 24, fontSize: 12, color: 'rgba(232, 221, 196, 0.55)' }}>
              <span style={{ fontFamily: 'var(--sp-font-mono)', fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(232, 221, 196, 0.45)', marginRight: 8 }}>
                Connected dossier:
              </span>
              <Link href="/strategic-playbook/munger-lattice-of-models" style={{ color: 'var(--sp-aged-brass)' }}>
                Munger — Lattice of Mental Models + Inversion
              </Link>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @media (max-width: 700px) {
          .pre-mortem-options {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

function FailureModeCard({ failureMode, index }) {
  const probColor =
    failureMode.probability === 'Common'
      ? 'var(--sp-oxblood)'
      : failureMode.probability === 'Uncommon'
      ? 'var(--sp-cognac)'
      : 'var(--sp-aged-bronze)';

  return (
    <div
      style={{
        padding: '24px 26px',
        background: 'var(--sp-soft-charcoal)',
        border: '1px solid var(--sp-divider)',
        borderRadius: 'var(--sp-radius-card)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 3,
          height: '100%',
          background: probColor,
          opacity: 0.7,
        }}
      />
      <div style={{ paddingLeft: 12 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 10,
          }}
        >
          <div
            style={{
              fontFamily: 'var(--sp-font-mono)',
              fontSize: 10,
              color: 'rgba(232, 221, 196, 0.55)',
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
            }}
          >
            Failure Mode {index}
          </div>
          <span
            style={{
              fontFamily: 'var(--sp-font-mono)',
              fontSize: 10,
              padding: '3px 10px',
              border: `1px solid ${probColor}`,
              borderRadius: 'var(--sp-radius)',
              color: probColor,
              letterSpacing: '0.10em',
              textTransform: 'uppercase',
            }}
          >
            {failureMode.probability}
          </span>
        </div>
        <h4
          style={{
            fontFamily: 'var(--sp-font-serif)',
            fontWeight: 500,
            fontSize: 19,
            lineHeight: 1.3,
            color: 'var(--sp-warm-cream)',
            margin: '0 0 12px 0',
          }}
        >
          {failureMode.mode}
        </h4>
        <p
          style={{
            fontSize: 13,
            lineHeight: 1.65,
            color: 'rgba(232, 221, 196, 0.80)',
            margin: '0 0 16px 0',
          }}
        >
          {failureMode.description}
        </p>

        <div style={{ marginBottom: 14 }}>
          <div
            style={{
              fontFamily: 'var(--sp-font-mono)',
              fontSize: 9,
              textTransform: 'uppercase',
              letterSpacing: '0.14em',
              color: 'var(--sp-aged-brass)',
              marginBottom: 6,
            }}
          >
            Watch for
          </div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {(failureMode.warningSignals || []).map((s, i) => (
              <li
                key={i}
                style={{
                  fontSize: 12,
                  lineHeight: 1.6,
                  color: 'rgba(232, 221, 196, 0.78)',
                  marginBottom: 4,
                }}
              >
                {s}
              </li>
            ))}
          </ul>
        </div>

        <div
          style={{
            paddingTop: 12,
            borderTop: '1px solid var(--sp-divider)',
          }}
        >
          <div
            style={{
              fontFamily: 'var(--sp-font-mono)',
              fontSize: 9,
              textTransform: 'uppercase',
              letterSpacing: '0.14em',
              color: 'var(--sp-forest-moss)',
              marginBottom: 4,
            }}
          >
            Mitigation
          </div>
          <div
            style={{
              fontSize: 12,
              lineHeight: 1.55,
              color: 'rgba(232, 221, 196, 0.85)',
            }}
          >
            {failureMode.mitigation}
          </div>
        </div>
      </div>
    </div>
  );
}

function ToolTopbar() {
  return (
    <div className="sp-topbar">
      <div>
        <h1>Strategic Intelligence</h1>
        <div className="sp-topbar-meta">Tools · Pre-Mortem Generator</div>
      </div>
      <nav className="sp-topbar-nav">
        <Link href="/strategic-playbook">Dashboard</Link>
        <Link href="/strategic-playbook/library">Library</Link>
        <Link href="/strategic-playbook/tools" className="sp-active">Tools</Link>
        <Link href="/strategic-playbook/decisions">Decisions</Link>
        <Link href="/strategic-playbook/simulate">Simulate</Link>
      </nav>
    </div>
  );
}
